import { View, html, CustomEventWithDetail } from 'rune-ts';
import klass from './RadioGroup.module.scss';
import { ButtonRadio } from '../../atoms/ButtonRadio/ButtonRadio';
import { find } from '@fxts/core';

type Selectable<T = unknown> = {
  // unique id
  id: string;

  display_name: string;

  value?: T;

  default?: boolean;
};

/**
 * do not export. use `this.changeEvent`
 */
class ButtonGroupChangeEvent<Value> extends CustomEventWithDetail<Selectable<Value> | undefined> {}

export type RadioGroupOption = {
  readonly?: boolean;
  direction?: 'vertical' | 'horizontal';
  custom_klass?: {
    group?: string;
    item?: string;
  };
};

interface IRadioGroupStyle {
  theme: {
    direction: string;
  };
}

export type RadioGroupData<T, Item extends Selectable<T> = Selectable<T>> = {
  name?: string;
  items: Item[];
  selected_id?: Item['id'];

  // eslint-disable-next-line no-use-before-define
  onChange?: (view: RadioGroup<T>) => void;
};

export class RadioGroup<Value, Data extends RadioGroupData<Value> = RadioGroupData<Value>>
  extends View<Data>
  implements IRadioGroupStyle
{
  // private selected: Value;
  private readonly option: RadioGroupOption | undefined;

  private selected?: Selectable<Value>;

  private readonly onChangeHook: RadioGroupData<Value>['onChange'];

  constructor(data: Readonly<Data>, option?: RadioGroupOption) {
    super(data, option);

    this.option = option;
    this.onChangeHook = this.data.onChange;
    this.setSelected(data.selected_id);
  }

  private findById(id: unknown): Selectable<Value> | undefined {
    return find((item) => id === item.id, this.data.items);
  }

  public setSelected(id?: Data['selected_id']): this {
    const default_item = find((item) => item.default === true, this.data.items);
    this.selected = this.findById(id) ?? default_item;
    this.redraw();
    return this;
  }

  public get selected_id(): Data['selected_id'] {
    return this.selected?.id;
  }

  public getSelected(): Selectable<Value> | undefined {
    return find((item) => item.id === this.selected_id, this.data.items);
  }

  override template() {
    const { custom_klass = {} } = this.option ?? {};
    const selected_item = this.getSelected();

    return html`<div class="${klass.group} ${custom_klass.group} ${this.theme.direction}">
      ${this.data.items.map(
        (item) =>
          new ButtonRadio({
            name: this.data.name,
            id: item.id,
            label: item.display_name,
            active: selected_item && selected_item.id === item.id,
            disabled: Boolean(this.option?.readonly),
            styles: {
              wrapper: `${klass.item} ${custom_klass.item}`,
              label: custom_klass.item,
            },
          }),
      )}
    </div>`;
  }

  override onRender() {
    if (this.option?.readonly) return;

    this.delegate('click', ButtonRadio, (_e, view) => {
      const { id } = view.data;
      this.setSelected(id);
      this.redraw();

      this.dispatchEvent(ButtonGroupChangeEvent<Value>, {
        bubbles: true,
        detail: this.getSelected(),
      });

      if (this.onChangeHook) this.onChangeHook(this);

      this.redraw();
    });
  }

  /**
   * @example
   * ```ts
   *  override onRender() {
   *     this.delegate(this.radioGroupView.changeEvent, this.radioGroupView.viewClass, (e, view) => {
   *       const detail = e.detail; // type: `Selectable<Value> | undefined`
   *     });
   *   }
   * ```
   */
  public get changeEvent(): typeof ButtonGroupChangeEvent<Value> {
    return ButtonGroupChangeEvent<Value>;
  }

  /**
   * @example
   * ```ts
   *  override onRender() {
   *     this.delegate(this.radioGroupView.changeEvent, this.radioGroupView.viewClass, (e, view) => {
   *       const detail = e.detail; // type: `Selectable<Value> | undefined`
   *     });
   *   }
   * ```
   */
  public get viewClass(): typeof RadioGroup<Value> {
    return RadioGroup<Value>;
  }

  get theme() {
    return {
      direction: (this.option?.direction === 'vertical' ? klass.vertical : klass.horizontal) as string,
    };
  }
}
